import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import { movies, categories, getMoviesByCategory, getCategoryName } from '@/mocks/movies';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'dongzuo');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeSlug]);

  useEffect(() => {
    if (slug && slug !== activeSlug) {
      setIsLoading(true);
      setActiveSlug(slug);
      const timer = setTimeout(() => setIsLoading(false), 150);
      return () => clearTimeout(timer);
    } else if (slug) {
      setActiveSlug(slug);
    }
  }, [slug, activeSlug]);

  const categoryMovies = getMoviesByCategory(activeSlug);
  const categoryName = getCategoryName(activeSlug);

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        name: `麻花影院 - ${categoryName}`,
        url: `https://mahua-cinema.readdy.ai/category/${activeSlug}`,
        isPartOf: { '@type': 'WebSite', name: '麻花影院' },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://mahua-cinema.readdy.ai' },
          { '@type': 'ListItem', position: 2, name: categoryName, item: `https://mahua-cinema.readdy.ai/category/${activeSlug}` },
        ],
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16">
          <Breadcrumb />

          <main>
            {/* Category Header */}
            <section className="px-4 md:px-6 py-6 md:py-10">
              <div className="max-w-7xl mx-auto">
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-2">
                  {categoryName}
                </h1>
                <p className="text-sm md:text-base text-foreground-500">
                  共找到 <strong className="text-primary-400">{categoryMovies.length}</strong> 部{categoryName}
                </p>
              </div>
            </section>

            {/* Category Tabs */}
            <section className="px-4 md:px-6 pb-4">
              <div className="max-w-7xl mx-auto">
                <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      onClick={() => {
                        setActiveSlug(cat.slug);
                        window.scrollTo(0, 0);
                      }}
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                        activeSlug === cat.slug
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-950'
                      }`}
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className={`${cat.icon} text-sm`}></i>
                      </div>
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>
            </section>

            {/* Movies Grid */}
            <section className="px-4 md:px-6 py-4 md:py-8">
              <div className="max-w-7xl mx-auto">
                <div
                  className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 transition-opacity duration-150 ${
                    isLoading ? 'opacity-50' : 'opacity-100'
                  }`}
                >
                  {categoryMovies.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>

                {categoryMovies.length === 0 && (
                  <div className="text-center py-20">
                    <div className="w-16 h-16 mx-auto flex items-center justify-center text-foreground-400">
                      <i className="ri-movie-line text-4xl"></i>
                    </div>
                    <p className="mt-4 text-foreground-500">暂无{categoryName}资源，敬请期待</p>
                  </div>
                )}
              </div>
            </section>

            {/* Other Categories */}
            <section className="px-4 md:px-6 py-6 md:py-10">
              <div className="max-w-7xl mx-auto">
                <h4 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-4">
                  其他分类推荐
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {categories
                    .filter((c) => c.slug !== activeSlug)
                    .map((cat) => {
                      const sampleMovies = movies.filter((m) => m.category === cat.slug).slice(0, 4);
                      return (
                        <Link
                          key={cat.slug}
                          to={`/category/${cat.slug}`}
                          onClick={() => {
                            setActiveSlug(cat.slug);
                            window.scrollTo(0, 0);
                          }}
                          className="bg-background-100 rounded-lg p-4 hover:bg-background-200 transition-colors cursor-pointer"
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-5 h-5 flex items-center justify-center text-primary-400">
                              <i className={cat.icon}></i>
                            </div>
                            <span className="font-semibold text-sm text-foreground-950 whitespace-nowrap">{cat.name}</span>
                          </div>
                          <div className="flex -space-x-2">
                            {sampleMovies.map((m) => (
                              <img
                                key={m.id}
                                src={m.image}
                                alt={m.title}
                                className="w-10 h-14 rounded object-cover border-2 border-background-100"
                                loading="lazy"
                              />
                            ))}
                          </div>
                          <p className="text-xs text-foreground-500 mt-2">{getMoviesByCategory(cat.slug).length} 部影片</p>
                        </Link>
                      );
                    })}
                </div>
              </div>
            </section>
          </main>
        </div>
        <Footer />
      </div>
    </>
  );
}